/**
 * Saving the trip document without overwriting what someone else just changed.
 *
 * Every editor in the app builds its save the same way — `{ ...tripConfig,
 * destinations: next }` — so a save carrying one changed field also carries a
 * complete copy of every other field as that client last saw it. The write was
 * `setDoc(..., { merge: true })`, which merges at the key level and replaces
 * arrays wholesale. That's enough to stop a save deleting a field it never
 * mentioned, and no help at all for the fields it always mentions: two admins
 * editing two different destinations at the same time both sent a full
 * `destinations` array, and whoever landed second silently erased the other's
 * work. Same for `travellers` and `accommodations`.
 *
 * Two changes fix it, in order of how much they do:
 *
 * 1. **Only write what actually changed.** A field the caller didn't touch
 *    (`before` equals `after`) is left out of the write entirely, so a title
 *    edit can no longer revert a remote destination change, and none of these
 *    saves can revert a membership change — which this path was never the
 *    authority on, as the old comment in App.tsx already said.
 *
 * 2. **Merge the arrays by identity** when both clients did change one, using
 *    the same policy and the same code as a day's item lists (mergeItems):
 *    per-entry field changes apply on top of whatever is now stored, an entry
 *    neither client touched is untouched, and only two clients changing the
 *    SAME field of the SAME entry is a conflict — which rejects visibly rather
 *    than resolving itself, so the loser's form stays open with an error
 *    instead of their edit disappearing.
 *
 * The baseline is the editor's own view of the trip, which is exactly what it
 * spread into the object it passed. It is read from the caller's live trip
 * state rather than threaded through all ten call sites.
 *
 * ponytail: destinations have no stored id, so a visit is identified by its
 * slug plus its arrival date — where and when, because a trip can return to
 * the same city and two Paris visits keyed on name alone would collide.
 * Renaming a destination or moving its arrival date therefore reads as a delete
 * plus an add, so doing either at the same moment as someone edits that same
 * destination is a conflict rather than a merge. Give Destination a real id if
 * that starts mattering.
 */

import { doc, runTransaction, type Firestore } from 'firebase/firestore';
import type { TripConfig } from '../types';
import { stripUndef } from './dayWrites';

type Body = Record<string, unknown>;
import { mergeTripConfig } from "../../functions/src/tripConfigMerge.mjs";
export { mergeTripConfig } from "../../functions/src/tripConfigMerge.mjs";

/**
 * Commit one editor's trip-document changes in a transaction.
 *
 * `body` is what the old code sent — the stripped config, the caller's complete
 * view. `previous` is the same shape for the trip as that editor had it before
 * the edit. Returns false when nothing needed writing.
 */
/** The route: what the server validates against every traveller's branches. */
const ROUTE_FIELDS = ['destinations', 'startDate', 'endDate'] as const;
const pick = (body: Body, keys: readonly string[]) => Object.fromEntries(keys.filter(k => k in body).map(k => [k, body[k]]));
const omit = (body: Body, keys: readonly string[]) => Object.fromEntries(Object.entries(body).filter(([k]) => !keys.includes(k)));
export type RouteWriter = (tripId: string, before: Body, after: Body) => Promise<{ changed: boolean } | false>;

export async function writeTripConfig(db: Firestore, tripId: string, previous: Body, body: Body, routeWriter?: RouteWriter): Promise<boolean> {
  const before = stripUndef(previous), after = stripUndef(body);
  // Route changes go through the server (changeTripRoute), which can see every
  // branch; everything else keeps this client transaction. `false` from the
  // writer means the function isn't reachable yet: write the route here as
  // before (refused once the rules close direct route writes).
  let routeChanged = false, routeViaServer = false;
  const routeBefore = pick(before, ROUTE_FIELDS), routeAfter = pick(after, ROUTE_FIELDS);
  if (routeWriter && JSON.stringify(routeBefore) !== JSON.stringify(routeAfter)) {
    const result = await routeWriter(tripId, routeBefore, routeAfter);
    if (result !== false) { routeViaServer = true; routeChanged = result.changed; }
  }
  const rest = await runTransaction(db, async transaction => {
    const ref = doc(db, 'trips', tripId);
    const snapshot = await transaction.get(ref);
    if (!snapshot.exists()) throw new Error('This trip is no longer available.');
    const changes = mergeTripConfig(snapshot.data() as Body,
      routeViaServer ? omit(before, ROUTE_FIELDS) : before, routeViaServer ? omit(after, ROUTE_FIELDS) : after);
    if (!Object.keys(changes).length) return false;
    transaction.set(ref, changes, { merge: true });
    return true;
  });
  return rest || routeChanged;
}

/** The baseline a save should merge against: the editor's own view of the trip. */
export const configBaseline = (trips: TripConfig[], id: string): TripConfig | undefined =>
  trips.find(trip => trip.id === id);
